import { useState, type ReactNode } from 'react'
import { Clock, Mail } from 'lucide-react'
import { cn } from '@/lib/utils'
import { Button } from '@/components/ui/shadcn/button'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/shadcn/dialog'
import {
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectLabel,
  SelectTrigger,
} from '@/components/ui/shadcn/select'
import {
  DAY_NAMES,
  type AutomationPlatform,
  type DayOfWeek,
} from '@backend/lib/db/schema/automation'
import { DAY_SHORT, PLATFORM_LABELS, togglePlatform } from './constants'
import { SourceChip } from './source-chip'
import { sourceName, type AutomationSource, type MoodboardLike } from './source'
import type { AutomationGroup } from './grouping'
import { formatDayList, indefiniteArticle } from './schedule'
import { PlatformIcon } from './platform-icon'
import { AutomationFlowCanvas, type FlowSelection } from './automation-flow-canvas'
import { StarsIcon } from '@/components/ui/icons/icons-new'

export type AutomationDraft = {
  daysOfWeek: DayOfWeek[]
  platforms: AutomationPlatform[]
  sendReminders: boolean
  moodboardId: number
}

interface Props {
  open: boolean
  onOpenChange: (open: boolean) => void
  /** All moodboards — only `ready` ones are offered as sources. */
  moodboards: MoodboardLike[]
  /** present → edit mode (whole group); absent → create mode */
  group?: AutomationGroup
  defaultDay: DayOfWeek
  isSaving: boolean
  onSubmit: (draft: AutomationDraft) => void
  timezone: string
}

function initialMoodboardId(
  group: AutomationGroup | undefined,
  readyMoodboards: MoodboardLike[],
): number | undefined {
  if (group) return group.moodboardId
  return readyMoodboards[0]?.id
}

function PanelHeading({ icon, title }: { icon: ReactNode; title: string }) {
  return (
    <h3 className="text-foreground flex items-center gap-2 text-sm font-semibold">
      {icon}
      {title}
    </h3>
  )
}

export function AutomationDialog({
  open,
  onOpenChange,
  moodboards,
  group,
  defaultDay,
  isSaving,
  onSubmit,
  timezone,
}: Props) {
  const isEdit = !!group
  const readyMoodboards = moodboards.filter((m) => m.status === 'ready')
  const [moodboardId, setMoodboardId] = useState<number | undefined>(() =>
    initialMoodboardId(group, readyMoodboards),
  )
  const [days, setDays] = useState<DayOfWeek[]>(group?.days ?? [defaultDay])
  const [platforms, setPlatforms] = useState<AutomationPlatform[]>(group?.platforms ?? ['linkedin'])
  const [selection, setSelection] = useState<FlowSelection>('trigger')
  // create → always active; edit → preserve the existing value (dialog no
  // longer exposes this control — it's the card's Active/Paused switch now).
  const [sendReminders] = useState(group?.sendReminders ?? true)

  const toggleDay = (day: DayOfWeek) => {
    setDays((current) => {
      if (current.includes(day)) {
        // Keep at least one day selected.
        return current.length === 1 ? current : current.filter((d) => d !== day)
      }
      return DAY_NAMES.filter((d) => current.includes(d) || d === day)
    })
  }

  // Hydrate against all moodboards (not just ready) so editing an automation
  // whose board regressed to analyzing still shows its name.
  const selected: AutomationSource | undefined =
    moodboardId != null
      ? { id: moodboardId, moodboard: moodboards.find((m) => m.id === moodboardId) }
      : undefined
  const selectedName = selected ? sourceName(selected) : 'your moodboard'
  const connector = 'from'
  const canSubmit = moodboardId != null && platforms.length > 0 && days.length > 0 && !isSaving

  const platformList = platforms.map((p) => PLATFORM_LABELS[p]).join(' + ')

  const addPlatform = (platform: AutomationPlatform) => {
    setPlatforms((current) => togglePlatform(current, platform))
  }

  const removePlatform = (platform: AutomationPlatform) => {
    setPlatforms((current) => {
      if (current.length === 1) return current
      return current.filter((p) => p !== platform)
    })
    // Don't leave the panel pointing at a node that no longer exists.
    if (selection === `platform:${platform}`) setSelection('generate')
  }

  const selectionPanel = () => {
    if (selection === 'trigger') {
      return (
        <div className="flex flex-col gap-3">
          <PanelHeading
            icon={<Clock className="text-muted-foreground h-4 w-4" />}
            title="Trigger"
          />
          <div className="grid grid-cols-7 gap-1">
            {DAY_NAMES.map((d) => {
              const on = days.includes(d)
              return (
                <button
                  key={d}
                  type="button"
                  onClick={() => toggleDay(d)}
                  aria-pressed={on}
                  className={cn(
                    'rounded-lg py-1.5 text-xs font-medium transition-colors',
                    on ? 'bg-primary text-primary-foreground' : 'text-foreground hover:bg-hover',
                  )}>
                  {DAY_SHORT[d]}
                </button>
              )
            })}
          </div>
          <p className="text-muted-foreground flex items-center gap-1.5 text-xs">
            <Clock className="h-3.5 w-3.5" />
            Runs at 8:00 · {timezone}
          </p>
        </div>
      )
    }

    if (selection === 'generate') {
      return (
        <div className="flex flex-col gap-3">
          <PanelHeading
            icon={<StarsIcon width={14} height={14} color="var(--foreground)" />}
            title="Generate a post"
          />
          <Select
            value={moodboardId != null ? String(moodboardId) : undefined}
            onValueChange={(v) => {
              const id = Number(v)
              if (!Number.isNaN(id)) setMoodboardId(id)
            }}>
            <SelectTrigger>{selected && <SourceChip source={selected} size="sm" />}</SelectTrigger>
            <SelectContent>
              {readyMoodboards.length > 0 && (
                <SelectGroup>
                  <SelectLabel>Moodboards</SelectLabel>
                  {readyMoodboards.map((m) => (
                    <SelectItem key={`moodboard-${m.id}`} value={String(m.id)}>
                      <SourceChip source={{ id: m.id, moodboard: m }} size="sm" />
                    </SelectItem>
                  ))}
                </SelectGroup>
              )}
            </SelectContent>
          </Select>
          <p className="text-muted-foreground text-xs">
            The moodboard Cassi draws voice and style from.
          </p>
        </div>
      )
    }

    if (selection === 'email') {
      return (
        <div className="flex flex-col gap-3">
          <PanelHeading
            icon={<Mail className="text-muted-foreground h-4 w-4" />}
            title="Email you a review link"
          />
          <p className="text-muted-foreground text-xs">
            Sent to your account email when the post is ready to review.
          </p>
        </div>
      )
    }

    const platform = selection.slice('platform:'.length) as AutomationPlatform
    const isLastPlatform = platforms.length === 1
    return (
      <div className="flex flex-col gap-3">
        <PanelHeading
          icon={<PlatformIcon platform={platform} size="xs" />}
          title={PLATFORM_LABELS[platform]}
        />
        <p className="text-muted-foreground text-xs">
          Cassi tailors the caption for {PLATFORM_LABELS[platform]}.
        </p>
        <div>
          <Button
            variant="outline"
            size="sm"
            disabled={isLastPlatform}
            title={isLastPlatform ? 'At least one platform required' : undefined}
            onClick={() => removePlatform(platform)}
            className="text-destructive hover:text-destructive">
            Remove {PLATFORM_LABELS[platform]}
          </Button>
          {isLastPlatform && (
            <p className="text-muted-foreground mt-1.5 text-xs">At least one platform required.</p>
          )}
        </div>
      </div>
    )
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="flex h-[88vh] max-h-[900px] w-[95vw] max-w-6xl flex-col gap-0 overflow-hidden p-0 sm:rounded-xl md:flex-row">
        {/* LEFT: config panel */}
        <div className="border-border/60 flex min-h-0 w-full flex-1 flex-col border-b md:w-[360px] md:flex-none md:border-b-0 md:border-r">
          <DialogHeader className="p-4 pb-3">
            <DialogTitle>{isEdit ? 'Edit automation' : 'New automation'}</DialogTitle>
            <DialogDescription
              aria-live="polite"
              className="bg-royal-blue-50 text-foreground dark:bg-royal-blue-950/40 rounded-lg px-3 py-2.5 text-sm">
              Every <span className="font-semibold">{formatDayList(days)}</span>, Cassi drafts{' '}
              {indefiniteArticle(platformList)}{' '}
              <span className="font-semibold">{platformList}</span> post {connector}{' '}
              <span className="font-semibold">{selectedName}</span> and emails you.
            </DialogDescription>
          </DialogHeader>

          <div className="min-h-0 flex-1 overflow-y-auto p-4 pt-2">{selectionPanel()}</div>

          <div className="border-border/60 flex justify-end gap-2 border-t p-4">
            <Button
              variant="outline"
              size="sm"
              onClick={() => onOpenChange(false)}
              disabled={isSaving}>
              Cancel
            </Button>
            <Button
              variant="primary"
              size="sm"
              disabled={!canSubmit}
              onClick={() => {
                if (moodboardId == null || days.length === 0) return
                onSubmit({ daysOfWeek: days, platforms, sendReminders, moodboardId })
              }}>
              {isEdit ? 'Save changes' : 'Create automation'}
            </Button>
          </div>
        </div>

        {/* RIGHT: flow canvas — visually first on mobile (order-first) so the
            flow reads as an overview above the config panel; DOM order keeps
            the title first for screen readers. */}
        <AutomationFlowCanvas
          days={days}
          timezone={timezone}
          source={selected}
          platforms={platforms}
          selected={selection}
          onSelect={setSelection}
          onAddPlatform={addPlatform}
          onRemovePlatform={removePlatform}
          className="order-first h-[45%] min-h-0 shrink-0 md:order-none md:h-auto md:flex-1"
        />
      </DialogContent>
    </Dialog>
  )
}
